/**
 * Checks if an item or its submenus have access.
 * ! Note: Do not modify this function. It is intended for navigation purposes only.
 *
 * @param {Object} item - The navigation item being checked.
 * @returns {boolean} - Returns true if the item or its submenu has access, otherwise false.
 */
const hasAccess = (item) => {
  // Check directly if the item has access
  if (item.hasAccess) {
    return true;
  }

  // If the item has submenus, check if any of the submenus have access
  if (item.submenu) {
    return item.submenu.some((subItem) => subItem.hasAccess);
  }

  // If neither the parent nor the submenus have access, return false
  return false;
};

/**
 * Filters navigation items based on access.
 * ! Note: Do not modify this function. It is intended for navigation purposes only.
 *
 * @param {Array} items - The list of navigation items to be filtered.
 * @returns {Array} - New navigation items that have access.
 */
export const filterNavItemsWithAccess = (items) => {
  return items.reduce((filteredItems, item) => {
    // First, check if the item has access
    if (hasAccess(item)) {
      // If it has access, add the item to the results
      const filteredItem = { ...item };

      // If it has submenus, filter them
      if (item.submenu) {
        filteredItem.submenu = item.submenu.filter(
          (subItem) => subItem.hasAccess
        );
      }

      filteredItems.push(filteredItem);
    }
    return filteredItems;
  }, []);
};

/**
 * Groups navigation items by their groupKey property.
 * If a group does not already exist in the accumulator,
 * it creates a new group with its groupLabel and an empty items array.
 * Finally, it pushes the current navigation item into the appropriate group.
 * ! Note: Do not modify this function. It is intended for navigation purposes only.
 *
 * @param {Array} items - List of navigation items to be grouped
 * @returns {Object} - An object where keys are groupKeys and values are grouped navigation items
 */
export const groupNavByKey = (items) => {
  return items.reduce((acc, item) => {
    // Filter out items with no path and no submenu items
    if (!item.path && (!item.submenu || item.submenu.length === 0)) {
      return acc; // Skip this item
    }

    if (!acc[item.groupKey]) {
      acc[item.groupKey] = {
        groupLabel: item.groupLabel,
        items: [],
      };
    }

    acc[item.groupKey].items.push(item);
    return acc;
  }, {});
};

/**
 * Checks if the user has at least one of the required roles.
 * ! Note: Do not modify this function. It is intended for navigation purposes only.
 *
 * @param {Array} userRoles - The roles assigned to the user.
 * @param {Array} requiredRoles - The roles required to access a resource.
 * @returns {Boolean} - Returns true if the user has a matching role, otherwise false.
 */
export const hasRequiredRoutesRoles = (userRoles, requiredRoles) => {
  if (!userRoles && userRoles.length < 1) {
    return false;
  }

  return requiredRoles?.length
    ? requiredRoles.some((role) => userRoles.includes(role))
    : false;
};

/**
 * Checks if the user has all the required permissions.
 * ! Note: Do not modify this function. It is intended for navigation purposes only.
 *
 * @param {Array} userPermissions - The permissions assigned to the user.
 * @param {Array} requiredPermissions - The permissions required to access a resource.
 * @returns {Boolean} - Returns true if the user has all required permissions, otherwise false.
 */

export const hasRequiredRoutesPermissions = (
  userPermissions,
  requiredPermissions
) => {
  if (!userPermissions && userPermissions.length < 1) {
    return false;
  }

  return requiredPermissions?.length
    ? requiredPermissions.every((perm) => userPermissions.includes(perm))
    : true;
};

/**
 * Checks if the user has at least one of the required roles for actions.
 * ! Note: Do not modify this function. It is intended for action-level checks only.
 *
 * @param {Array} userRoles - The roles assigned to the user.
 * @param {Array} requiredRoles - The roles required to perform an action.
 * @returns {Boolean} - Returns true if the user has a matching role, otherwise false.
 */
export const hasRequiredActionsRoles = (userRoles, requiredRoles) => {
  if (!Array.isArray(userRoles) || !Array.isArray(requiredRoles)) {
    return false;
  }

  if (userRoles.length < 1) {
    return false;
  }

  const hasAccessRoles = requiredRoles.some((role) => userRoles.includes(role));

  return hasAccessRoles;
};

/**
 * Checks if the user has at least one of the required permissions for actions.
 * ! Note: Do not modify this function. It is intended for action-level checks only.
 *
 * @param {Array} userPermissions - The permissions assigned to the user.
 * @param {Array} requiredPermissions - The permissions required to perform an action.
 * @returns {Boolean} - Returns true if the user has a matching permission, otherwise false.
 */
export const hasRequiredActionsPermissions = (
  userPermissions,
  requiredPermissions
) => {
  if (!Array.isArray(userPermissions) || !Array.isArray(requiredPermissions)) {
    return false;
  }

  if (userPermissions.length < 1) {
    return false;
  }

  const hasAccessPermissions = requiredPermissions.some((permission) =>
    userPermissions.includes(permission)
  );

  return hasAccessPermissions;
};

/**
 * Checks if the user has the required roles and permissions for a navigation item.
 * ! Note: Do not modify this function. It is intended for navigation purposes only.
 *
 * @param {Array} requiredRoles - The array of roles required for the item. Can be null or empty.
 * @param {Array} requiredPermissions - The array of permissions required for the item. Can be null or empty.
 * @param {Array} userRoles - The array of roles the user has.
 * @param {Array} userPermissions - The array of permissions the user has.
 * @returns {boolean} - True if the user has access, false otherwise.
 */
export const checkAccess = (
  requiredRoles,
  requiredPermissions,
  userRoles,
  userPermissions
) => {
  const rolesCheck =
    !requiredRoles ||
    requiredRoles.length === 0 ||
    requiredRoles.some((role) => userRoles.includes(role));

  const permissionsCheck =
    !requiredPermissions ||
    requiredPermissions.length === 0 ||
    requiredPermissions.some((permission) =>
      userPermissions.includes(permission)
    );

  return rolesCheck && permissionsCheck;
};

/**
 * Filters navigation items based on user's roles and permissions.
 * ! Note: Do not modify this function. It is intended for navigation purposes only.
 *
 * @param {Array} navItems - The array of navigation items to filter.
 * @param {Array} userRoles - The array of roles the user has.
 * @param {Array} userPermissions - The array of permissions the user has.
 * @returns {Array} - The filtered array of navigation items that the user has access to.
 */
export const getAccessibleItems = (navItems, userRoles, userPermissions) => {
  return navItems.reduce((acc, item) => {
    const hasAccess = checkAccess(
      item.requiredRoles,
      item.requiredPermissions,
      userRoles,
      userPermissions
    );

    const isBypass = item.bypassAuthCheck;
    const hasPath = Boolean(item.path);
    const hasSubmenu = Array.isArray(item.submenu) && item.submenu.length > 0;

    if (hasPath) {
      if (isBypass) {
        acc.push(item);
      } else if (hasAccess) {
        acc.push(item);
      }
    } else if (hasSubmenu) {
      const filteredSubmenu = item.submenu.reduce((submenuAcc, sub) => {
        const subHasAccess = checkAccess(
          sub.requiredRoles,
          sub.requiredPermissions,
          userRoles,
          userPermissions
        );

        const hasSubItems =
          Array.isArray(sub.subItem) && sub.subItem.length > 0;

        let filteredSubItems = [];

        if (hasSubItems) {
          filteredSubItems = sub.subItem.filter((subItem) =>
            checkAccess(
              subItem.requiredRoles,
              subItem.requiredPermissions,
              userRoles,
              userPermissions
            )
          );
        }

        if (subHasAccess || filteredSubItems.length > 0) {
          submenuAcc.push({
            ...sub,
            subItem: filteredSubItems,
          });
        }

        return submenuAcc;
      }, []);

      if (hasAccess || filteredSubmenu.length > 0) {
        acc.push({
          ...item,
          submenu: filteredSubmenu,
        });
      }
    }

    return acc;
  }, []);
};

/**
 * True when the logged-in user is looking at their own employee profile from
 * outside their portfolio — an HLB accountant (admin) who is also an employee
 * of HLB TM. That profile, its leave and its timesheet are the employee's own
 * view: visible, but not managed by them. The server decides the flag
 * (`ownProfileSelfOnly` in the login payload) and refuses the writes anyway;
 * this only keeps the manager controls out of sight.
 *
 * @param {Object} user - Logged-in user from the auth store.
 * @param {string} employeeId - Employee being shown.
 * @returns {boolean}
 */
export const isOwnSelfOnlyProfile = (user, employeeId) =>
  !!user?.ownProfileSelfOnly &&
  !!employeeId &&
  String(user?.employee) === String(employeeId);
